import { useState, useEffect } from "react";
import {
  loadPrices,
  PriceItem,
  clearPriceCache,
} from "src/helpers/priceLoader";

export interface UsePriceLoaderReturn {
  prices: PriceItem[];
  isLoading: boolean;
  error: string | null;
  refreshPrices: () => Promise<void>;
  clearCache: () => void;
}

export function usePriceLoader(): UsePriceLoaderReturn {
  const [prices, setPrices] = useState<PriceItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPricesData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const loadedPrices = await loadPrices();
      setPrices(loadedPrices);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load prices";
      setError(errorMessage);
      console.error("Price loading error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshPrices = async () => {
    clearPriceCache();
    await loadPricesData();
  };

  const clearCache = () => {
    clearPriceCache();
  };

  useEffect(() => {
    loadPricesData();
  }, []);

  return {
    prices,
    isLoading,
    error,
    refreshPrices,
    clearCache,
  };
}

// Utility function to preload prices (can be called in App.tsx or index.tsx)
export async function preloadPrices(): Promise<void> {
  try {
    await loadPrices();
    console.log("Prices preloaded successfully");
  } catch (error) {
    console.error("Failed to preload prices:", error);
  }
}
